import { useCallback, useEffect, useState } from "react";

import {
  Autocomplete,
  Chip,
  CircularProgress,
  debounce,
  TextField,
} from "@mui/material";
import { listSubmissionReport, SubmissionSummary } from "@rubi/api-generated";
import { useParams } from "react-router";

import StyledBadge from "@/common/StyledBadge";
import { formatDateShort } from "@/common/utils";
import { reportColourTypes, statusTypes } from "@/types/typesReportV2";

import { getReportTitle } from "./utils";

interface LinkSupplementaryDropdownProps {
  selectedReportToLink: SubmissionSummary | null;
  setSelectedReportToLink: (report: SubmissionSummary | null) => void;
  setIsLinkingDialogOpen: (open: boolean) => void;
}

const LinkSupplementaryDropdown = ({
  selectedReportToLink,
  setSelectedReportToLink,
  setIsLinkingDialogOpen,
}: LinkSupplementaryDropdownProps) => {
  const { orgId } = useParams();
  const [reportList, setReportList] = useState<SubmissionSummary[]>([]);
  const [loading, setLoading] = useState(false);

  const fetchReports = async (searchText: string) => {
    setLoading(true);
    const submissions = (
      await listSubmissionReport({
        query: {
          time_created_min: null,
          time_created_max: null,
          time_updated_min: null,
          time_updated_max: null,
          status: null,
          source: null,
          first_name: null,
          last_name: null,
          email: null,
          phone_number: null,
          secondary_phone_number: null,
          incident_number: null,
          tracking_number: null,
          limit: 10,
          offset: 0,
          survey_name: null,
          search:
            searchText && searchText?.trim().length > 0 ? searchText : null,
          assigned_to: null,
          image_search_id: null,
          image_similarity: null,
          sort_order: "desc",
          org_id: orgId ?? "",
        },
      })
    ).data!;
    const filteredReports = submissions.submissions.filter(
      (report) => report.is_supplemental === false,
    );
    setReportList(filteredReports);
    setLoading(false);
  };

  const debouncedFetchReports = useCallback(debounce(fetchReports, 300), [
    orgId,
  ]);

  useEffect(() => {
    fetchReports("");
  }, []);

  return (
    <Autocomplete
      loading={loading}
      options={[...reportList]}
      getOptionLabel={(option) => {
        const title = getReportTitle(
          option.is_supplemental,
          option.survey_name ?? "",
        );
        return `${option.tracking_number} ${option.incident_number ? `(${option.incident_number})` : ""} - ${title} - ${formatDateShort(
          option.time_created,
        )}`;
      }}
      value={selectedReportToLink ?? null}
      onChange={(event, newValue) => {
        if (newValue && newValue.id) {
          setSelectedReportToLink(newValue);
          setIsLinkingDialogOpen(true);
        } else {
          setSelectedReportToLink(null);
        }
      }}
      onInputChange={(event, newValue) => {
        debouncedFetchReports(newValue);
      }}
      size="medium"
      renderInput={(params) => (
        <TextField
          {...params}
          label="Select Report to Link to Supplementary Report"
          variant="outlined"
          placeholder="Type to search by tracking number, incident number, or name"
          fullWidth
          slotProps={{
            input: {
              ...params.InputProps,
              endAdornment: (
                <>
                  {loading ? (
                    <CircularProgress color="inherit" size={20} />
                  ) : null}
                  {params.InputProps.endAdornment}
                </>
              ),
            },
          }}
        />
      )}
      renderOption={(props, option) => {
        const title = getReportTitle(
          option.is_supplemental,
          option.survey_name ?? "",
        );
        return (
          <li
            {...props}
            style={{
              width: "100%",
              padding: "8px 12px",
              backgroundColor: "var(--background-paper)",
              borderRadius: "8px",
              marginBottom: "8px",
              boxShadow:
                "0 1px 3px rgba(0, 0, 0, 0.12), 0 1px 2px rgba(0, 0, 0, 0.24)",
              display: "flex",
              alignItems: "center",
              transition: "all 0.3s ease",
              cursor: "pointer",
            }}
            onMouseEnter={(e) =>
              (e.currentTarget.style.boxShadow = "0 4px 8px rgba(0, 0, 0, 0.1)")
            }
            onMouseLeave={(e) =>
              (e.currentTarget.style.boxShadow =
                "0 1px 3px rgba(0, 0, 0, 0.12), 0 1px 2px rgba(0, 0, 0, 0.24)")
            }
          >
            <StyledBadge
              color="error"
              variant="dot"
              invisible={
                option.status !== "submitted" && option.status !== "resubmitted"
              }
              anchorOrigin={{
                vertical: "top",
                horizontal: "left",
              }}
            >
              <Chip
                style={{
                  opacity:
                    option.status === "rejected" || option.status === "approved"
                      ? 0.6
                      : 1,
                  maxWidth: "100px",
                  textOverflow: "ellipsis",
                  overflow: "hidden",
                  whiteSpace: "nowrap",
                }}
                label={
                  statusTypes[option.status]
                    ? statusTypes[option.status]
                    : option.status
                }
                color={
                  reportColourTypes[option.status]
                    ? reportColourTypes[option.status]
                    : "default"
                }
              />
            </StyledBadge>
            <div style={{ marginLeft: "12px", flex: 1 }}>
              <div style={{ display: "flex", justifyContent: "space-between" }}>
                <span style={{ fontWeight: "bold" }}>
                  {option.tracking_number}{" "}
                  {option.incident_number ? `(${option.incident_number})` : ""}
                </span>
                <span style={{ fontStyle: "italic", color: "#666" }}>
                  {formatDateShort(option.time_created)}
                </span>
              </div>
              <div>
                {title} - {option.first_name} {option.last_name}
              </div>
            </div>
          </li>
        );
      }}
      sx={{
        width: "600px",
      }}
      filterOptions={(options, params) => {
        const filtered = options.filter((option) => {
          return (
            (option.survey_name || "survey_name")
              .toLowerCase()
              .includes(params.inputValue.toLowerCase()) ||
            option.id.toLowerCase().includes(params.inputValue.toLowerCase()) ||
            `${option.first_name} ${option.last_name}`
              .toLowerCase()
              .includes(params.inputValue.toLowerCase()) ||
            formatDateShort(option.time_created)
              .toLowerCase()
              .includes(params.inputValue.toLowerCase()) ||
            option.tracking_number
              ?.toLowerCase()
              .includes(params.inputValue.toLowerCase()) ||
            option.incident_number
              ?.toLowerCase()
              .includes(params.inputValue.toLowerCase())
          );
        });

        return filtered;
      }}
      noOptionsText="No reports found"
    />
  );
};

export default LinkSupplementaryDropdown;
